'use client';
import React from 'react';

import classnames from 'classnames';
import { DateTime } from 'luxon';

import { Modal } from '@benzinga/core-ui';

interface Props {
  closeModal: () => void;
  createWatchlist: (watchlistName: string) => void;
}

interface State {
  readonly name: string;
  readonly showReqField: boolean;
}

export const WatchlistCreate: React.FC<Props> = props => {
  const [state, setState] = React.useState<State>({
    name: '',
    showReqField: false,
  });

  const NAME_FIELD = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (NAME_FIELD.current) {
      NAME_FIELD.current.focus();
    }
  });

  const onChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(() => ({
      name: event.target.value,
      showReqField: false,
    }));
  }, []);

  const doCreate = React.useCallback(() => {
    const name = state.name;
    if (!name || name.trim().length === 0) {
      setState(old => ({ ...old, showReqField: true }));
    } else {
      const createWatchlist = props.createWatchlist;
      const closeModal = props.closeModal;
      createWatchlist(name);
      closeModal();
    }
  }, [props.closeModal, props.createWatchlist, state.name]);

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        doCreate();
      }
    },
    [doCreate],
  );

  const { name, showReqField } = state;
  const { closeModal } = props;
  const buttonDisabled = !name || name.trim().length === 0;

  return (
    <Modal onClose={closeModal} size={{ alert: true }} title="Create A New Watchlist">
      <div className="Modal-content">
        <label className={classnames('Modal-label', { 'Modal-label--warning': showReqField })}>
          New Watchlist Name
        </label>

        <input
          autoFocus
          className={classnames('TUTORIAL_Watchlist-Modal-Name-Input', 'Modal-input', 'Modal-input--text', {
            'Input--warning': showReqField,
          })}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={`My ${DateTime.local().toFormat('MMM yyyy')} Portfolio`}
          ref={NAME_FIELD}
          type="text"
          value={name}
        />

        <div className="Modal-proceed">
          <button className="Button Button--text" onClick={closeModal}>
            Cancel
          </button>

          <button
            className={classnames('Button', {
              'Button--disabled': buttonDisabled,
              'Button--primary': !buttonDisabled,
            })}
            onClick={doCreate}
          >
            Create Watchlist
          </button>
        </div>
      </div>
    </Modal>
  );
};
